import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import axios, { type AxiosError } from "axios";
import {
  AdminProductsApi,
  type AdminProductsApiAdminProductsProductIdSetupProgressPatchRequest,
  type AdminProductsApiAdminProductsSkuIdUnpublishPatchRequest,
  type CreateDowngradeProductRequest,
  type CreateUpgradeProductRequest,
  type ProductSerialType,
  type ProductSkuDto,
  PublicInventoryLocationType,
} from "mws-catalog-ts-sdk";
import {
  AdminAthleteFixtureDetailsApi,
  AdminClaimApi,
  AdminFixtureDetailsApi,
  type AdminMediaContentRequest,
  AdminProductsApi as AdminProductsApiV2,
  type AdminProductsApiV2AdminProductsProductIdAiDescriptionPostRequest,
  type AdminProductsApiV2AdminProductsProductIdInventoryLocationPutRequest,
  type AdminProductsApiV2AdminProductsProductIdLinkFramedProductPatchRequest,
  type AdminProductsApiV2AdminProductsProductIdMediaContentUrlsPutRequest,
  AdminTranslationsApi,
  type CreateNewProductRequest,
  type CreateOrUpdateAthleteFixtureDetailsRequest,
  type CreateOrUpdateFixtureDetailsRequest,
  type FramingType,
  type MediasPerProduct,
  type ResolveExternalSportsEntitiesRequest,
  type SortDirection,
  type SortProductOn,
  type UpdateNewProductRequest,
  InventoryLocationType as V2InventoryLocationType,
} from "mws-catalog-ts-sdk-v2-admin";
import { ProductsApi as PublicProductsApiV2 } from "mws-catalog-ts-sdk-v2-public";
import type { AthleteDto } from "mws-sports-ts-sdk";
import { type MaybeRef, type MaybeRefOrGetter, type Ref, unref } from "vue";

import { transformTranslationKeys } from "@/common/api/translationQueries";
import type { Guid, UseQueryOptionsOmitQueryKey } from "@/common/types";
import { getEventsByProductListingIds } from "@/products/api/services";
import { type ProductEvent } from "@/products/types";

/**
 * Maps catalog SDK enum values to v2 SDK enum values.
 * The catalog SDK generates snake case enum values (_short, _long) due to SnakeCaseSchemaFilter,
 * but the v2 SDK expects the original enum names (short, long).
 */
const mapStorageLocationTypesToBackendFormat = (
  storageLocationTypes?: PublicInventoryLocationType[],
): V2InventoryLocationType[] | undefined => {
  if (!storageLocationTypes) return undefined;

  return storageLocationTypes.map((type) => {
    // Convert catalog SDK enum values to v2 SDK enum values
    switch (type) {
      case PublicInventoryLocationType.Short:
        return V2InventoryLocationType.Short;
      case PublicInventoryLocationType.Long:
        return V2InventoryLocationType.Long;
      case PublicInventoryLocationType.Processing:
        return V2InventoryLocationType.Processing;
      case PublicInventoryLocationType.Special:
        return V2InventoryLocationType.Special;
      case PublicInventoryLocationType.Shipping:
        return V2InventoryLocationType.Shipping;
      case PublicInventoryLocationType.Framing:
        return V2InventoryLocationType.Framing;
      case PublicInventoryLocationType.Shipped:
        return V2InventoryLocationType.Shipped;
      case PublicInventoryLocationType.ToLong:
        return V2InventoryLocationType.ToLong;
      case PublicInventoryLocationType.Odoo:
        return V2InventoryLocationType.Odoo;
      case PublicInventoryLocationType.Box:
        return V2InventoryLocationType.Box;
      case PublicInventoryLocationType.Rack:
        return V2InventoryLocationType.Rack;
      case PublicInventoryLocationType.Unknown:
      default:
        return V2InventoryLocationType.Unknown;
    }
  });
};

const catalogApiUrl = import.meta.env.VITE_CATALOG_API;

const adminProductApi = new AdminProductsApi(undefined, catalogApiUrl);
const adminProductApiV2 = new AdminProductsApiV2(undefined, catalogApiUrl);
const adminTranslationsApi = new AdminTranslationsApi(undefined, catalogApiUrl);
const adminClaimApi = new AdminClaimApi(undefined, catalogApiUrl);
const adminAthleteFixtureDetailsApi = new AdminAthleteFixtureDetailsApi(
  undefined,
  catalogApiUrl,
);
const adminFixtureDetailsApi = new AdminFixtureDetailsApi(
  undefined,
  catalogApiUrl,
);
const publicProductsApiV2 = new PublicProductsApiV2(undefined, catalogApiUrl);

export const useGetEventsByProductListingIdsQuery = (
  skuIds: Ref<string[] | undefined>,
  options?: UseQueryOptionsOmitQueryKey<ProductEvent[]>,
) => {
  const queryKey = ["events", skuIds];
  const queryFn = () => getEventsByProductListingIds(skuIds);

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  });
};

export const useAthleteProductsQuery = (athleteId: Ref<AthleteDto["id"]>) => {
  const queryKey = ["productsByAthlete", athleteId];
  const queryFn = async () => {
    const response = await adminProductApi.adminProductsAthletesAthleteIdGet({
      athleteId: athleteId.value,
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn });
};

export const usePublishProductListingMutation = (
  onSuccess?: (data: ProductSkuDto) => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  const onSuccessCallback = (response: ProductSkuDto) => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["events"] });
    onSuccess?.(response);
  };

  return useMutation({
    mutationFn: async (skuId: string) => {
      const response = await adminProductApi.adminProductsSkuIdPublishPatch({
        skuId,
      });

      return response.data;
    },
    onSuccess: onSuccessCallback,
    onError,
  });
};

export const useUnpublishProductListingMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const onSuccessCallback = () => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["events"] });
    onSuccess?.();
  };

  return useMutation({
    mutationFn: async (
      request: AdminProductsApiAdminProductsSkuIdUnpublishPatchRequest,
    ) => {
      const response =
        await adminProductApi.adminProductsSkuIdUnpublishPatch(request);

      return response.data;
    },
    onSuccess: onSuccessCallback,
  });
};

export const useCreateDowngradeProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateDowngradeProductRequest) => {
      const response = await adminProductApi.adminProductsDowngradePost({
        createDowngradeProductRequest: request,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useCreateUpgradeProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateUpgradeProductRequest) => {
      const response = await adminProductApi.adminProductsUpgradePost({
        createUpgradeProductRequest: request,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useCopyProductListingToEventMutation = (
  onSuccess: (data: Guid) => void,
  onError: (error: AxiosError) => void,
) => {
  return useMutation({
    mutationFn: async (request: {
      productListingId: Guid;
      eventId: Guid;
      priceInEuroCents: number;
    }) => {
      const response =
        await adminProductApi.adminProductsProductListingIdEventEventIdPost(
          request,
        );

      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useCreateListingForProductOnEventMutation = (
  onSuccess: (data: Guid) => void,
  onError: (error: AxiosError) => void,
) => {
  return useMutation({
    mutationFn: async (request: {
      productId: Guid;
      eventId: Guid;
      priceInEuroCents: number;
    }) => {
      const response = await adminProductApi.adminProductsProductIdListingsPost(
        {
          productId: request.productId,
          createListingForProductRequest: {
            eventId: request.eventId,
            priceInEuroCents: request.priceInEuroCents,
          },
        },
      );

      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useGenerateAiProductsDescriptionsMutation = (
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productIds: string[];
      extraInformation: string | null;
    }) => {
      const response =
        await adminProductApiV2.v2AdminProductsAiDescriptionsPost({
          generateAiProductsDescriptionsRequest: {
            productIds: request.productIds,
            extraInformation: request.extraInformation,
          },
        });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError,
  });
};

export const useUpdateProductSetupProgressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminProductsApiAdminProductsProductIdSetupProgressPatchRequest,
    ) => {
      const response =
        await adminProductApi.adminProductsProductIdSetupProgressPatch(request);

      return response.data;
    },
    onSuccess(_, request) {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });

      queryClient.invalidateQueries({
        queryKey: ["products", request.productId],
      });
    },
  });
};

export const useCreateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateNewProductRequest) => {
      const response = await adminProductApiV2.v2AdminProductsPost({
        createNewProductRequest: request,
      });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useGetProductsListQuery = (
  searchTerm?: Ref,
  pageSize?: Ref,
  offset?: Ref,
  filters?: Ref<{
    teamId: string;
    athleteId: string;
    sortOn?: SortProductOn;
    sortDirection?: SortDirection;
    shippingOfficeId?: string;
    hasStock?: boolean;
    hasListings?: boolean;
    framingType?: FramingType;
    isReceived?: boolean;
    isLinkedToFrame?: boolean;
    serialType?: ProductSerialType;
    properties?: string[];
    categoryIds?: string[];
    locationTypes?: PublicInventoryLocationType[];
  }>,
) => {
  const queryKey = ["products", { searchTerm, offset, pageSize, filters }];

  const queryFn = async () => {
    let hasStock = filters?.value.hasStock;
    let hasListings = filters?.value.hasListings;
    let isReceived = filters?.value.isReceived;
    let isLinkedToFrame = filters?.value.isLinkedToFrame;

    if (filters?.value.properties && filters.value.properties.length > 0) {
      const properties = filters.value.properties;

      if (properties.includes("hasStock")) hasStock = true;

      if (properties.includes("noStock")) hasStock = false;

      if (properties.includes("hasListings")) hasListings = true;

      if (properties.includes("noListings")) hasListings = false;

      if (properties.includes("isReceived")) isReceived = true;

      if (properties.includes("isNotReceived")) isReceived = false;

      if (properties.includes("isLinkedToFrame")) isLinkedToFrame = true;

      if (properties.includes("isNotLinkedToFrame")) isLinkedToFrame = false;
    }

    return (
      await adminProductApiV2.v2AdminProductsGet({
        searchTerm: searchTerm?.value,
        offset: offset?.value,
        pageSize: pageSize?.value,
        sortProductOn: filters?.value.sortOn,
        sortDirection: filters?.value.sortDirection,
        athleteId: filters?.value.athleteId,
        teamId: filters?.value.teamId,
        shippingOfficeId: filters?.value.shippingOfficeId,
        hasStock: hasStock,
        hasListings: hasListings,
        framingType: filters?.value.framingType,
        isReceived: isReceived,
        isLinkedToFrame: isLinkedToFrame,
        serialType: filters?.value.serialType,
        categoryIds: filters?.value.categoryIds,
        locationTypes: mapStorageLocationTypesToBackendFormat(
          filters?.value.locationTypes,
        ),
      })
    ).data;
  };

  const options = { keepPreviousData: true };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetAthleteFixtureDetailsQuery = (
  productId: MaybeRef<string | undefined>,
  options?: {
    includeExternalIds?: boolean;
    enabled?: MaybeRefOrGetter<boolean>;
  },
) => {
  const key = [
    "products",
    productId,
    "athleteFixtureDetails",
    { includeExternalIds: options?.includeExternalIds ?? false },
  ];

  const query = async () => {
    const id = unref(productId);

    if (!id) {
      throw new Error("productId is required");
    }

    return (
      await adminAthleteFixtureDetailsApi.v2AdminAthleteFixtureDetailsProductsProductIdGet(
        {
          productId: id,
          includeExternalIds: options?.includeExternalIds,
        },
      )
    ).data;
  };

  return useQuery({
    queryKey: key,
    queryFn: query,
    enabled: options?.enabled,
  });
};

export const useGetFixtureDetailsByProductQuery = (
  productId: MaybeRef<string | undefined>,
) => {
  const key = ["products", productId, "fixtureDetails"];

  const query = async () => {
    const id = unref(productId);

    if (!id) {
      throw new Error("productId is required");
    }

    return (
      await adminFixtureDetailsApi.v2AdminFixtureDetailsProductsProductIdGet({
        productId: id,
      })
    ).data;
  };

  return useQuery({
    queryKey: key,
    queryFn: query,
  });
};

export const usePutProductFixtureDetailsMutation = (
  productId: MaybeRef<string | undefined>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      fixtureDetailsId: string;
      fixtureDetails: CreateOrUpdateFixtureDetailsRequest;
    }) => {
      const response =
        await adminFixtureDetailsApi.v2AdminFixtureDetailsFixtureDetailsIdPut({
          fixtureDetailsId: request.fixtureDetailsId,
          createOrUpdateFixtureDetailsRequest: request.fixtureDetails,
        });

      return response.data;
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["products", unref(productId), "fixtureDetails"],
      });
    },
  });
};

export const useRemoveProductFixtureDetailsMutation = (
  productId: MaybeRef<string | undefined>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const id = unref(productId);
      if (!id) throw new Error("productId is required");
      await axios.delete(
        `${catalogApiUrl}/v2/admin/fixture-details/products/${id}`,
      );
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["products", unref(productId), "fixtureDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["products", unref(productId)],
      });
    },
  });
};

export const useGetProductQuery = (
  productId: MaybeRef<string | undefined>,
  options?: {
    enabled?: MaybeRefOrGetter<boolean | undefined>;
  },
) => {
  const key = ["products", productId];

  const query = async () => {
    const id = unref(productId);

    if (!id) {
      throw new Error("productId is required");
    }

    return (
      await adminProductApiV2.v2AdminProductsIdGet({
        id,
      })
    ).data;
  };

  return useQuery({
    queryKey: key,
    queryFn: query,
    enabled: options?.enabled,
  });
};

export const useGetProductsLinkedFramesQuery = (
  productId: string | undefined,
) => {
  const key = ["linked-frames", productId];
  const query = async () => {
    if (!productId) {
      throw new Error("productId is required");
    }

    return (
      await adminProductApiV2.v2AdminProductsProductIdLinkedFramesGet({
        productId: productId,
      })
    ).data;
  };

  return useQuery({
    queryKey: key,
    queryFn: query,
  });
};

export const getSerialScanData = async (serial: string) => {
  const response = await adminProductApiV2.v2AdminProductsSerialScandataGet({
    serial: serial,
  });

  return response.data;
};

export const getPublicProduct = async (
  productIdentifier: string,
  platformKey: string,
) => {
  const response = await publicProductsApiV2.v2ProductsProductIdentifierGet({
    productIdentifier,
    xPlatformKey: platformKey,
  });

  return response.data;
};

export const usePostAthleteFixtureDetailsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      athleteFixtureDetails: CreateOrUpdateAthleteFixtureDetailsRequest;
    }) => {
      const response =
        await adminAthleteFixtureDetailsApi.v2AdminAthleteFixtureDetailsProductsProductIdPost(
          {
            productId: request.productId,
            createOrUpdateAthleteFixtureDetailsRequest:
              request.athleteFixtureDetails,
          },
        );

      return response.data;
    },
    onSuccess(_, request) {
      queryClient.invalidateQueries({
        queryKey: ["products", request.productId, "athleteFixtureDetails"],
      });
    },
  });
};

export const usePostAthleteFixtureDetailsFromExternalMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      payload: ResolveExternalSportsEntitiesRequest;
    }) => {
      const response =
        await adminAthleteFixtureDetailsApi.v2AdminAthleteFixtureDetailsProductsProductIdFromExternalPost(
          {
            productId: request.productId,
            resolveExternalSportsEntitiesRequest: request.payload,
          },
        );

      return response.data;
    },
    onSuccess(_, request) {
      queryClient.invalidateQueries({
        queryKey: ["products", request.productId, "athleteFixtureDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["products", request.productId, "fixtureDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["products", request.productId],
      });
    },
  });
};

export const usePutAthleteFixtureDetailsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      athleteFixtureDetailsId: string;
      athleteFixtureDetails: CreateOrUpdateAthleteFixtureDetailsRequest;
    }) => {
      const response =
        await adminAthleteFixtureDetailsApi.v2AdminAthleteFixtureDetailsAthleteFixtureDetailsIdPut(
          {
            athleteFixtureDetailsId: request.athleteFixtureDetailsId,
            createOrUpdateAthleteFixtureDetailsRequest:
              request.athleteFixtureDetails,
          },
        );

      return response.data;
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
    },
  });
};

export const usePatchProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      updateNewProductRequest: UpdateNewProductRequest;
    }) => {
      const response =
        await adminProductApiV2.v2AdminProductsProductIdPatch(request);

      return response.data;
    },
    async onSuccess(_data, variables) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["products", variables.productId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["productTranslations", variables.productId],
        }),
      ]);
    },
  });
};

export const useGenerateAiProductDescriptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminProductsApiV2AdminProductsProductIdAiDescriptionPostRequest,
    ) => {
      await adminProductApiV2.v2AdminProductsProductIdAiDescriptionPost(
        request,
      );
    },
    async onSuccess(_data, variables) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["products", variables.productId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["productTranslations", variables.productId],
        }),
      ]);
    },
  });
};

export const usePatchFramedProductMutation = () => {
  return useMutation({
    mutationFn: async (
      request: AdminProductsApiV2AdminProductsProductIdLinkFramedProductPatchRequest,
    ) => {
      return await adminProductApiV2.v2AdminProductsProductIdLinkFramedProductPatch(
        request,
      );
    },
  });
};

export const useChangeProductInventoryLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminProductsApiV2AdminProductsProductIdInventoryLocationPutRequest,
    ) => {
      const response =
        await adminProductApiV2.v2AdminProductsProductIdInventoryLocationPut(
          request,
        );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["picklistitems"] });
    },
  });
};

export const usePatchProductMediaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      mediaContentDtoArray: Array<AdminMediaContentRequest>;
    }) => {
      const response =
        await adminProductApiV2.v2AdminProductsProductIdMediaContentPatch({
          productId: request.productId,
          adminMediaContentRequest: request.mediaContentDtoArray,
        });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useUploadProductMediaUrlsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      mediaContentUrls: Array<string>;
      origin: string;
    }) => {
      const adminProductsApiV2AdminProductsProductIdMediaContentUrlsPostRequest: AdminProductsApiV2AdminProductsProductIdMediaContentUrlsPutRequest =
        {
          productId: request.productId,
          updateProductMediaUrlsRequest: {
            mediaContentUrls: request.mediaContentUrls,
            origin: request.origin,
          },
        };
      const response =
        await adminProductApiV2.v2AdminProductsProductIdMediaContentUrlsPut(
          adminProductsApiV2AdminProductsProductIdMediaContentUrlsPostRequest,
        );

      return response.data;
    },

    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["products", vars.productId] });
      queryClient.invalidateQueries({ queryKey: ["events", "products"] });
    },
  });
};

export const useBulkUpdateProductMediaUrlsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MediasPerProduct[]) => {
      const response =
        await adminProductApiV2.v2AdminProductsBulkUploadMediaContentsPut({
          bulkUpdateProductMediaUrlsRequest: {
            productMedias: request,
          },
        });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["events", "products"] });
    },
  });
};

export const PRODUCT_TRANSLATION_KEYS = {
  description: "Description",
  functionalDescription: "FunctionalDescription",
  specialTierTitle: "SpecialTierTitle",
  specialTierDescription: "SpecialTierDescription",
} as const;

export type ProductTranslationKey =
  (typeof PRODUCT_TRANSLATION_KEYS)[keyof typeof PRODUCT_TRANSLATION_KEYS];

const productTranslationsQueryKey = (
  productId: string,
  translationKey: ProductTranslationKey,
) => ["productTranslations", productId, translationKey] as const;

export const productTranslationsQueryOptions = (
  productId: string,
  translationKey: ProductTranslationKey,
) =>
  queryOptions({
    queryKey: productTranslationsQueryKey(productId, translationKey),
    queryFn: async () => {
      const response =
        await adminTranslationsApi.v2AdminTranslationsEntityEntityIdKeyGet({
          entityId: productId,
          key: translationKey,
        });

      return transformTranslationKeys(response.data);
    },
    enabled: !!productId,
  });

export const useUpdateProductTranslationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      translationKey: ProductTranslationKey;
      language: string;
      value: string;
    }) => {
      await adminTranslationsApi.v2AdminTranslationsEntityEntityIdPut({
        entityId: request.productId,
        updateTranslationRequest: {
          key: request.translationKey,
          language: request.language,
          value: request.value,
          isAiGenerated: false,
        },
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: productTranslationsQueryKey(
          variables.productId,
          variables.translationKey,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ["products", variables.productId],
      });
    },
  });
};

export const useTranslateProductTextsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      productId: string;
      keys: ProductTranslationKey[];
    }) => {
      await adminProductApiV2.v2AdminProductsProductIdTranslationsPost({
        productId: request.productId,
        translateProductTextsRequest: { keys: request.keys },
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["productTranslations", variables.productId],
      });
    },
  });
};

export const get360ImagesZipUrl = async (baseUrl: string) => {
  const response = await adminProductApiV2.v2AdminProductsMedia360ZipGet({
    baseUrl,
  });

  return response.data;
};

export const useProductClaimsCollectionQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{ productId?: string; userId?: string }>,
  options?: {
    enabled?: MaybeRef<boolean>;
  },
) => {
  return useQuery({
    queryKey: ["product_claims", { pageSize, offset, filters }],
    queryFn: async () => {
      const response = await adminClaimApi.v2AdminClaimsGet({
        ...(filters.value.userId && { userId: filters.value.userId }),
        ...(filters.value.productId && { productId: filters.value.productId }),
        pageSize: pageSize.value,
        offset: offset.value,
      });

      return response.data;
    },
    enabled: options?.enabled,
  });
};

export const useSyncProductInOdooMutation = (
  productId: MaybeRef<string | undefined>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const id = unref(productId);
      if (!id) throw new Error("productId is required");
      await axios.post(`${catalogApiUrl}/v2/admin/products/${id}/odoo`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", unref(productId)],
      });
    },
  });
};

export const useDeleteClaimMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { claimId: string }) => {
      const response = await adminClaimApi.v2AdminClaimsClaimIdDelete({
        claimId: request.claimId,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_claims"] });
    },
  });
};

export const useBulkCreateClaimsMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response = await adminClaimApi.v2AdminClaimsBulkUsersUserIdPost({
        userId: request.userId,
      });

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["product_claims"] });
      onSuccess();
    },
    onError,
  });
};
